import { useEffect, useState, useCallback, useRef } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { DocumentAttachmentList } from "@/components/DocumentAttachment";
import {
  Plus,
  Trash,
  WhatsappLogo,
  EnvelopeSimple,
  PencilSimple,
  Paperclip,
  ArrowCounterClockwise,
  MagnifyingGlass,
  Copy,
  Check,
  Package,
  ChatCircleText,
  X,
  CaretLeft,
  CaretRight
} from "@phosphor-icons/react";

export const SOURCE_LABELS = {
  global: "All sources",
  indiamart: "IndiaMART",
  meta_ads: "Meta Ads",
  google_form: "Google Form",
  csv_upload: "CSV Upload",
  manual: "Manual",
  google_drive: "Google Drive"
};
export const SOURCES = Object.keys(SOURCE_LABELS);

export const LEAD_STAGE_LABELS = {
  initial: "Lead Initial",
  fu1: "Follow-up 1",
  fu2: "Follow-up 2",
  fu3: "Follow-up 3",
  fu4: "Follow-up 4",
  fu5: "Follow-up 5",
  quotation: "Quotation Initial",
  qfu1: "Quote FU-1",
  qfu2: "Quote FU-2",
  qfu3: "Quote FU-3",
  qfu4: "Quote FU-4",
  qfu5: "Quote FU-5",
  other: "Other"
};

export const SERVICE_STAGE_LABELS = {
  start_fu1: "Start FU-1",
  start_fu2: "Start FU-2",
  start_fu3: "Start FU-3",
  exp_fu1: "Renewal FU-1",
  exp_fu2: "Renewal FU-2",
  exp_fu3: "Renewal FU-3",
  exp_fu4: "Renewal FU-4",
  exp_fu5: "Renewal FU-5",
  oncall_fu1: "On Call FU-1",
  oncall_fu2: "On Call FU-2",
  oncall_fu3: "On Call FU-3",
  fu1: "Out of Warranty / General FU-1",
  fu2: "Out of Warranty / General FU-2",
  fu3: "Out of Warranty / General FU-3",
  fu4: "Out of Warranty / General FU-4",
  fu5: "Out of Warranty / General FU-5",
  quotation: "Quotation Initial",
  quotation_sent: "Quotation Sent",
  qfu1: "Quote FU-1",
  qfu2: "Quote FU-2",
  qfu3: "Quote FU-3",
  qfu4: "Quote FU-4",
  qfu5: "Quote FU-5",
  payment_reminder: "Payment Reminder",
  pfu1: "Payment FU-1",
  pfu2: "Payment FU-2",
  pfu3: "Payment FU-3",
  pfu4: "Payment FU-4",
  pfu5: "Payment FU-5",
  other: "Other"
};

export const ALL_STAGE_LABELS = {
  ...LEAD_STAGE_LABELS,
  ...SERVICE_STAGE_LABELS
};

export const DEFAULT_SERVICE_TYPES = [
  { value: "warranty", label: "Warranty" },
  { value: "out_of_warranty", label: "Out of Warranty" },
  { value: "amc", label: "AMC" },
  { value: "out_of_amc", label: "Out of AMC" },
  { value: "on_call", label: "On Call" },
  { value: "register", label: "Register" },
  { value: "other", label: "Other" }
];

const PLACEHOLDERS = [
  { tag: "{name}", label: "Customer / Lead Name" },
  { tag: "{product}", label: "Product / Service" },
  { tag: "{company}", label: "Company" },
  { tag: "{card_no}", label: "Card / Contract No" },
  { tag: "{amount}", label: "Amount" },
  { tag: "{expiry_date}", label: "Expiry Date" },
  { tag: "{salesperson}", label: "Sales Rep Name" },
  { tag: "{salesperson_phone}", label: "Sales Rep Phone" },
  { tag: "{salesperson_email}", label: "Sales Rep Email" },
];

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [serviceTypes, setServiceTypes] = useState(DEFAULT_SERVICE_TYPES);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStage, setFilterStage] = useState("all");
  const [filterLeadType, setFilterLeadType] = useState("all");
  const [filterProduct, setFilterProduct] = useState("all");
  const [copiedId, setCopiedId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, pRes, stRes] = await Promise.all([
        api.get("/templates"),
        api.get("/products").catch(() => ({ data: [] })),
        api.get("/service-types").catch(() => ({ data: [] }))
      ]);
      setTemplates(tRes.data || []);
      setProducts(pRes.data || []);
      if (stRes.data && stRes.data.length > 0) {
        const list = stRes.data.filter(t => t.is_active !== false).map(t => ({
          value: t.key,
          label: t.name
        }));
        if (list.length > 0) setServiceTypes(list);
      }
    } catch (e) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Reset page to 1 when search or filter values change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterType, filterCategory, filterStage, filterLeadType, filterProduct, pageSize]);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/templates/${deleteConfirm.id}`);
      toast.success(`Template "${deleteConfirm.name}" deleted`);
      setDeleteConfirm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete template");
    }
  };

  const copyToClipboard = (template) => {
    navigator.clipboard.writeText(template.body);
    setCopiedId(template.id);
    toast.success("Template text copied!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getProdName = (code) => {
    if (!code || code === "all") return "All Products";
    const p = products.find(x => String(x.productCode) === String(code));
    return p ? p.productName : "All Products";
  };

  const filteredTemplates = templates.filter(t => {
    const q = searchQuery.toLowerCase().trim();
    const matchQuery = !q || t.name.toLowerCase().includes(q) || (t.body && t.body.toLowerCase().includes(q)) || (t.subject && t.subject.toLowerCase().includes(q));
    const matchType = filterType === "all" || t.type === filterType;
    const matchCategory = filterCategory === "all" || (t.category || "lead") === filterCategory;
    const matchStage = filterStage === "all" || t.stage === filterStage;
    const matchLeadType = filterLeadType === "all" || (t.lead_type || "global") === filterLeadType;
    const matchProduct = filterProduct === "all"
      ? true
      : filterProduct === "general"
        ? (!t.product_code || t.product_code === "all" || t.product_code === "")
        : String(t.product_code) === String(filterProduct);
    return matchQuery && matchType && matchCategory && matchStage && matchLeadType && matchProduct;
  });

  const resetFilters = () => {
    setSearchQuery("");
    setFilterType("all");
    setFilterCategory("all");
    setFilterStage("all");
    setFilterLeadType("all");
    setFilterProduct("all");
  };

  const isFiltered = searchQuery !== "" || filterType !== "all" || filterCategory !== "all" || filterStage !== "all" || filterLeadType !== "all" || filterProduct !== "all";

  // Pagination calculation
  const totalTemplates = filteredTemplates.length;
  const totalPages = Math.ceil(totalTemplates / pageSize) || 1;
  const startIdx = totalTemplates === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIdx = Math.min(page * pageSize, totalTemplates);
  const paginatedTemplates = filteredTemplates.slice((page - 1) * pageSize, page * pageSize);

  // KPI stats
  const waCount = templates.filter(t => t.type === "whatsapp").length;
  const emailCount = templates.filter(t => t.type === "email").length;
  const prodSpecificCount = templates.filter(t => t.product_code && t.product_code !== "all" && t.product_code !== "").length;

  return (
    <div className="space-y-6" data-testid="templates-page">

      {/* Master Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-[#E5E7EB] pb-6">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#6B7280] mb-1">
            <span>Master</span>
            <span>/</span>
            <span className="text-[#002FA7]">Template Master</span>
          </div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-[#111827]">
            Template Master
          </h1>
          <p className="text-sm text-[#4B5563] mt-1">
            Configure reusable messaging templates with stage, product mapping, and channel targeting.
          </p>
        </div>

        <button
          onClick={() => { setEditingTemplate(null); setShowModal(true); }}
          data-testid="add-template-btn"
          className="bg-[#002FA7] text-white font-bold px-4 py-2.5 text-sm hover:bg-[#002280] transition-colors flex items-center justify-center gap-2 shadow-sm shrink-0"
        >
          <Plus size={18} weight="bold" />
          <span>Add Template</span>
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white border border-[#E5E7EB] p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-50 text-[#002FA7] flex items-center justify-center rounded-lg">
            <ChatCircleText size={26} weight="duotone" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">Total Templates</div>
            <div className="text-2xl font-black text-[#111827]">{templates.length}</div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-emerald-50 text-emerald-600 flex items-center justify-center rounded-lg">
            <WhatsappLogo size={26} weight="duotone" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">WhatsApp</div>
            <div className="text-2xl font-black text-[#111827]">{waCount}</div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-indigo-50 text-[#6366F1] flex items-center justify-center rounded-lg">
            <EnvelopeSimple size={26} weight="duotone" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">Email</div>
            <div className="text-2xl font-black text-[#111827]">{emailCount}</div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-purple-50 text-purple-600 flex items-center justify-center rounded-lg">
            <Package size={26} weight="duotone" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">Product Specific</div>
            <div className="text-2xl font-black text-[#111827]">{prodSpecificCount}</div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white border border-[#E5E7EB] p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input
            type="text"
            placeholder="Search templates by name, subject, or message..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm border border-[#D1D5DB] focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filter Dropdowns */}
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold bg-white text-[#374151] focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          >
            <option value="all">All Types</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
          </select>

          <select
            value={filterProduct}
            onChange={e => setFilterProduct(e.target.value)}
            className="border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold bg-white text-[#374151] focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          >
            <option value="all">All Products</option>
            <option value="general">General Templates</option>
            {products.map(p => (
              <option key={p.productCode} value={p.productCode}>{p.productName}</option>
            ))}
          </select>

          <select
            value={filterCategory}
            onChange={e => { setFilterCategory(e.target.value); setFilterStage("all"); }}
            className="border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold bg-white text-[#374151] focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          >
            <option value="all">All Categories</option>
            <option value="lead">Lead</option>
            <option value="service">Service</option>
          </select>

          <select
            value={filterStage}
            onChange={e => setFilterStage(e.target.value)}
            className="border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold bg-white text-[#374151] focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          >
            <option value="all">All Stages</option>
            {filterCategory === "lead" ? (
              Object.entries(LEAD_STAGE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))
            ) : filterCategory === "service" ? (
              Object.entries(SERVICE_STAGE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))
            ) : (
              <>
                <optgroup label="Lead Stages">
                  {Object.entries(LEAD_STAGE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </optgroup>
                <optgroup label="Service Stages">
                  {Object.entries(SERVICE_STAGE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </optgroup>
              </>
            )}
          </select>

          <select
            value={filterLeadType}
            onChange={e => setFilterLeadType(e.target.value)}
            className="border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold bg-white text-[#374151] focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          >
            <option value="all">
              {filterCategory === "service" ? "All Service Types" : filterCategory === "lead" ? "All Lead Sources" : "All Sources / Types"}
            </option>
            {filterCategory === "service" ? (
              <>
                <option value="global">All Service Types</option>
                {serviceTypes.map(st => (
                  <option key={st.value} value={st.value}>{st.label}</option>
                ))}
              </>
            ) : filterCategory === "lead" ? (
              SOURCES.map(s => (
                <option key={s} value={s}>{SOURCE_LABELS[s]}</option>
              ))
            ) : (
              <>
                <optgroup label="Lead Sources">
                  {SOURCES.map(s => (
                    <option key={s} value={s}>{SOURCE_LABELS[s]}</option>
                  ))}
                </optgroup>
                <optgroup label="Service Types">
                  <option value="global">All Service Types</option>
                  {serviceTypes.map(st => (
                    <option key={st.value} value={st.value}>{st.label}</option>
                  ))}
                </optgroup>
              </>
            )}
          </select>

          {isFiltered && (
            <button
              onClick={resetFilters}
              data-testid="reset-template-filters-btn"
              title="Reset all filters"
              className="flex items-center gap-1 border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#374151] transition-colors"
            >
              <ArrowCounterClockwise size={13} weight="bold" /> Reset
            </button>
          )}
        </div>
      </div>

      {/* Row-Wise Data Table */}
      <div className="bg-white border border-[#E5E7EB] overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB] text-[11px] font-black uppercase tracking-wider text-[#6B7280]">
                <th className="py-3.5 px-4">Template Name</th>
                <th className="py-3.5 px-4">Channel</th>
                <th className="py-3.5 px-4">Target Product</th>
                <th className="py-3.5 px-4">Stage / Category</th>
                <th className="py-3.5 px-4">Source / Type</th>
                <th className="py-3.5 px-4">Message Snippet</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB] text-sm">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-[#6B7280]">
                    Loading templates...
                  </td>
                </tr>
              ) : paginatedTemplates.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-[#6B7280]">
                    No templates found matching your selected filters.
                  </td>
                </tr>
              ) : (
                paginatedTemplates.map((t) => {
                  const isWA = t.type === "whatsapp";
                  const isService = (t.category || "lead") === "service";
                  const prodName = getProdName(t.product_code);
                  const isSpecificProd = t.product_code && t.product_code !== "all" && t.product_code !== "";
                  const serviceTypeObj = serviceTypes.find(s => s.value === t.lead_type);
                  const typeLabel = isService
                    ? (t.lead_type === "global" || !t.lead_type ? "All Service Types" : (serviceTypeObj?.label || t.lead_type))
                    : (t.lead_type === "global" || !t.lead_type ? "All Sources" : (SOURCE_LABELS[t.lead_type] || t.lead_type));

                  return (
                    <tr key={t.id} className="hover:bg-[#F9FAFB] transition-colors" data-testid={`template-row-${t.id}`}>
                      {/* Template Name & Subject */}
                      <td className="py-3.5 px-4 align-top font-bold text-[#111827] max-w-[200px]">
                        <div>{t.name}</div>
                        {t.subject && (
                          <div className="text-xs font-normal text-[#6B7280] truncate mt-0.5" title={t.subject}>
                            Subject: {t.subject}
                          </div>
                        )}
                      </td>

                      {/* Channel */}
                      <td className="py-3.5 px-4 align-top whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-full ${
                          isWA ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-indigo-50 text-indigo-700 border border-indigo-200"
                        }`}>
                          {isWA ? <WhatsappLogo size={13} weight="bold" /> : <EnvelopeSimple size={13} weight="bold" />}
                          {isWA ? "WhatsApp" : "Email"}
                        </span>
                      </td>

                      {/* Target Product */}
                      <td className="py-3.5 px-4 align-top whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full ${
                          isSpecificProd ? "bg-purple-50 text-purple-700 border border-purple-200" : "bg-gray-100 text-gray-700 border border-gray-200"
                        }`}>
                          <Package size={12} weight="bold" />
                          {prodName}
                        </span>
                      </td>

                      {/* Stage / Category */}
                      <td className="py-3.5 px-4 align-top whitespace-nowrap">
                        <div className="text-xs font-bold text-[#111827]">{ALL_STAGE_LABELS[t.stage] || t.stage}</div>
                        <div className="text-[11px] text-[#6B7280] uppercase tracking-wider font-semibold">
                          {isService ? "Service" : "Lead"}
                        </div>
                      </td>

                      {/* Source / Type */}
                      <td className="py-3.5 px-4 align-top text-xs text-[#374151] whitespace-nowrap">
                        <span className="font-medium text-[#111827]">{typeLabel}</span>
                      </td>

                      {/* Message Content Snippet */}
                      <td className="py-3.5 px-4 align-top text-xs text-[#374151] max-w-xs">
                        <div className="line-clamp-2 leading-relaxed">
                          <TemplateBodyHighlight text={t.body} />
                        </div>
                        {t.attachments && t.attachments.length > 0 && (
                          <div className="mt-2">
                            <DocumentAttachmentList attachments={t.attachments} showThumbnails={false} />
                          </div>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 align-top text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => copyToClipboard(t)}
                            title="Copy message snippet"
                            className="p-1.5 text-[#6B7280] hover:text-[#002FA7] hover:bg-blue-50 rounded transition-colors"
                          >
                            {copiedId === t.id ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                          </button>
                          <button
                            onClick={() => { setEditingTemplate(t); setShowModal(true); }}
                            data-testid={`edit-template-${t.id}`}
                            title="Edit template"
                            className="p-1.5 text-[#6B7280] hover:text-[#002FA7] hover:bg-blue-50 rounded transition-colors"
                          >
                            <PencilSimple size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(t)}
                            data-testid={`del-template-${t.id}`}
                            title="Delete template"
                            className="p-1.5 text-[#6B7280] hover:text-[#EF4444] hover:bg-red-50 rounded transition-colors"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls Bar */}
      {totalTemplates > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white border border-[#E5E7EB] p-4 shadow-xs" data-testid="template-pagination">
          <div className="flex flex-wrap items-center gap-3 text-xs text-[#6B7280]">
            <div>
              Showing <b className="text-[#111827]">{startIdx}-{endIdx}</b> of <b className="text-[#111827]">{totalTemplates}</b> templates
            </div>
            <div className="flex items-center gap-1.5 border-l border-[#E5E7EB] pl-3">
              <span className="text-[#6B7280] font-semibold">Per page:</span>
              <select
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
                className="border border-[#D1D5DB] px-2 py-1 text-xs font-bold bg-white text-[#374151] focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              data-testid="page-first"
              title="First Page"
              className="border border-[#D1D5DB] px-2 py-1.5 text-xs font-bold hover:bg-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              «
            </button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              data-testid="page-prev"
              className="border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold hover:bg-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
            >
              <CaretLeft size={12} weight="bold" /> Prev
            </button>
            <div className="border border-[#111827] bg-[#111827] text-white px-3 py-1.5 text-xs font-bold font-mono" data-testid="page-current">
              {page} / {totalPages}
            </div>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              data-testid="page-next"
              className="border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold hover:bg-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
            >
              Next <CaretRight size={12} weight="bold" />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              data-testid="page-last"
              title="Last Page"
              className="border border-[#D1D5DB] px-2 py-1.5 text-xs font-bold hover:bg-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white border border-[#111827] p-6 max-w-md w-full shadow-2xl space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-black text-xl text-[#111827]">Delete Template?</h3>
            <p className="text-sm text-[#4B5563]">
              Are you sure you want to delete template <strong className="text-[#111827]">"{deleteConfirm.name}"</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="border border-[#D1D5DB] px-4 py-2 text-xs font-bold text-[#374151] hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="bg-[#EF4444] text-white font-bold px-4 py-2 text-xs hover:bg-[#DC2626]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <TemplateModal
          products={products}
          serviceTypes={serviceTypes}
          onClose={() => setShowModal(false)}
          onSaved={load}
          initialData={editingTemplate}
        />
      )}
    </div>
  );
}

{/* Helper component to highlight variable tokens like {name}, {product} inside text */}
function TemplateBodyHighlight({ text = "" }) {
  if (!text) return null;
  const regex = /(\{[a-zA-Z0-9_]+\})/g;
  const parts = text.split(regex);

  return (
    <span className="whitespace-pre-wrap">
      {parts.map((part, index) => {
        if (regex.test(part)) {
          return (
            <span
              key={index}
              className="inline-block bg-blue-100 text-[#002FA7] font-mono text-[10px] font-bold px-1 py-0.5 rounded border border-blue-200 mx-0.5"
            >
              {part}
            </span>
          );
        }
        return part;
      })}
    </span>
  );
}

function TemplateModal({ products = [], serviceTypes = DEFAULT_SERVICE_TYPES, onClose, onSaved, initialData }) {
  const [f, setF] = useState(
    initialData || {
      name: "",
      type: "whatsapp",
      subject: "",
      body: "",
      category: "lead",
      stage: "initial",
      lead_type: "global",
      product_code: "",
      attachments: []
    }
  );
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);

  const insertTag = (tag) => {
    if (!textareaRef.current) {
      set("body", (f.body || "") + " " + tag);
      return;
    }
    const el = textareaRef.current;
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const text = f.body || "";
    const updated = text.substring(0, start) + tag + text.substring(end);
    set("body", updated);

    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + tag.length, start + tag.length);
    }, 50);
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;
    try {
      setSaving(true);
      if (files.length === 1) {
        const formData = new FormData();
        formData.append("file", files[0]);
        const res = await api.post("/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        const newAttachments = [...(f.attachments || []), res.data.url];
        set("attachments", newAttachments);
        toast.success(`Attached "${files[0].name}"`);
      } else {
        const formData = new FormData();
        files.forEach(file => formData.append("files", file));
        const res = await api.post("/upload/multiple", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        const newUrls = (res.data?.files || []).map(x => x.url);
        const newAttachments = [...(f.attachments || []), ...newUrls];
        set("attachments", newAttachments);
        toast.success(`${newUrls.length} files attached`);
      }
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setSaving(false);
      e.target.value = null;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...f, product_code: f.product_code ? f.product_code : null };
    try {
      if (initialData?.id) {
        await api.patch(`/templates/${initialData.id}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/templates", payload);
        toast.success("Template saved");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const currentStages = (f.category === "service") ? SERVICE_STAGE_LABELS : LEAD_STAGE_LABELS;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white border border-[#111827] w-full max-w-3xl p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="add-template-modal"
      >
        <div className="flex justify-between items-center pb-2 border-b border-[#E5E7EB]">
          <h3 className="font-heading font-black text-2xl text-[#111827]">
            {initialData?.id ? "Edit Template" : "New Template"}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Template Name">
            <input
              value={f.name}
              onChange={e => set("name", e.target.value)}
              required
              placeholder="e.g. Initial Welcome - Jal Pure"
              data-testid="tpl-name"
              className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
            />
          </Field>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Field label="Category">
              <select
                value={f.category || "lead"}
                onChange={e => {
                  const nextCat = e.target.value;
                  setF(prev => ({
                    ...prev,
                    category: nextCat,
                    stage: nextCat === "service" ? "start_fu1" : "initial",
                    lead_type: "global"
                  }));
                }}
                className="w-full border border-[#D1D5DB] px-3 py-2 text-xs font-bold bg-white text-[#374151]"
              >
                <option value="lead">Lead</option>
                <option value="service">Service</option>
              </select>
            </Field>

            <Field label="Target Product">
              <select
                value={f.product_code || ""}
                onChange={e => set("product_code", e.target.value)}
                className="w-full border border-[#D1D5DB] px-3 py-2 text-xs font-bold bg-white text-[#374151]"
              >
                <option value="">All Products</option>
                {products.map(p => (
                  <option key={p.productCode} value={p.productCode}>{p.productName}</option>
                ))}
              </select>
            </Field>

            <Field label="Channel">
              <select
                value={f.type}
                onChange={e => set("type", e.target.value)}
                data-testid="tpl-type"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-xs font-bold bg-white text-[#374151]"
              >
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
              </select>
            </Field>

            <Field label="Stage">
              <select
                value={f.stage}
                onChange={e => set("stage", e.target.value)}
                data-testid="tpl-stage"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-xs font-bold bg-white text-[#374151]"
              >
                {Object.entries(currentStages).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </Field>

            <Field label={f.category === "service" ? "Service Type" : "Lead Source"}>
              <select
                value={f.lead_type || "global"}
                onChange={e => set("lead_type", e.target.value)}
                data-testid="tpl-leadtype"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-xs font-bold bg-white text-[#374151]"
              >
                {f.category === "service" ? (
                  <>
                    <option value="global">All Service Types</option>
                    {serviceTypes.map(st => (
                      <option key={st.value} value={st.value}>{st.label}</option>
                    ))}
                  </>
                ) : (
                  SOURCES.map(s => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)
                )}
              </select>
            </Field>
          </div>

          {f.type === "email" && (
            <Field label="Email Subject">
              <input
                value={f.subject}
                onChange={e => set("subject", e.target.value)}
                placeholder="Subject line..."
                data-testid="tpl-subject"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              />
            </Field>
          )}

          <Field label="Message Body">
            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase font-bold text-[#6B7280]">Click to insert:</span>
              {PLACEHOLDERS.map(p => (
                <button
                  type="button"
                  key={p.tag}
                  onClick={() => insertTag(p.tag)}
                  title={`Insert ${p.label}`}
                  className="bg-blue-50 hover:bg-blue-100 text-[#002FA7] text-[11px] font-mono font-bold px-2 py-0.5 rounded border border-blue-200 transition-colors"
                >
                  + {p.tag}
                </button>
              ))}
            </div>

            <textarea
              ref={textareaRef}
              value={f.body}
              onChange={e => set("body", e.target.value)}
              required
              rows={5}
              placeholder="Hi {name}, thanks for reaching out regarding {product}..."
              data-testid="tpl-body"
              className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
            />
          </Field>

          <Field label="Attachments">
            <input
              type="file"
              multiple
              accept=".pdf,application/pdf,image/*,video/*,.mp4,.mov,.webm,.3gp,.doc,.docx,.xls,.xlsx,.csv,.txt"
              onChange={handleFileUpload}
              disabled={saving}
              className="w-full border border-[#D1D5DB] px-3 py-1.5 text-xs mb-2 bg-white cursor-pointer"
            />
            {f.attachments && f.attachments.length > 0 && (
              <div className="mt-1">
                <DocumentAttachmentList
                  attachments={f.attachments}
                  onRemove={(idx) => set("attachments", f.attachments.filter((_, j) => idx !== j))}
                />
              </div>
            )}
          </Field>

          <div className="flex justify-end gap-2 pt-2 border-t border-[#E5E7EB]">
            <button
              type="button"
              onClick={onClose}
              className="border border-[#D1D5DB] px-5 py-2 text-sm font-bold text-[#374151]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="tpl-submit"
              className="bg-[#002FA7] text-white font-bold px-5 py-2 text-sm hover:bg-[#002280]"
            >
              {saving ? "Saving..." : "Save Template"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280]">{label}</label>
      {children}
    </div>
  );
}
